/** Hebrew display copy for curated practice problems. Math expressions stay symbolic. */

export type PracticeTemplatePartHebrew = {
  id: string;
  text: string;
  hints?: string[];
};

export type PracticeTemplateHebrew = {
  /** Same skill id as the English template. */
  skillId: string;
  /** Same inclusive span as the English template. 0 is kindergarten. */
  gradeMin: number;
  gradeMax: number;
  /** Hebrew name of the skill (כיתה labels live in practiceGrades). */
  skillLabel: string;
  name: string;
  prompt: string;
  hints?: string[];
  explanation?: string;
  parts?: PracticeTemplatePartHebrew[];
  /** English annotation value → Hebrew. Point letters are omitted. */
  annotations?: Record<string, string>;
};

export const PRACTICE_HEBREW_TEMPLATES: Record<string, PracticeTemplateHebrew> = {
  "algebra-linear-2x-plus-3": {
    skillId: "algebra.linear-one-step",
    gradeMin: 7,
    gradeMax: 8,
    skillLabel: "משוואה בשלב אחד",
    name: "פתרו 2x + 3 = 11",
    prompt: "פתרו 2x + 3 = 11\nפתרו עבור x:\n2x+3=11",
    hints: [
      "בטלו קודם את החיבור, כדי שביטוי ה-x יישאר לבדו.",
      "אחר כך חלקו את שני האגפים במקדם של x.",
    ],
    explanation: "מחסרים 3 משני האגפים ומקבלים 2x = 8, ואז מחלקים ב-2.",
  },
  "algebra-factor-quadratic": {
    skillId: "algebra.factor-quadratic",
    gradeMin: 10,
    gradeMax: 11,
    skillLabel: "פירוק ריבועית",
    name: "פרקו לגורמים את x² − 5x + 6",
    prompt: "פרקו לגורמים את x² − 5x + 6\nפרקו לגורמים באופן מלא:\nx^2-5x+6",
    hints: [
      "מצאו שני מספרים שמכפלתם 6 וסכומם −5.",
      "שני הגורמים יהיו מהצורה (x − n).",
    ],
    explanation:
      "האיבר החופשי הוא 6 והמקדם האמצעי הוא −5, לכן מכפלת שני המספרים היא 6 וסכומם −5. המספרים הם −2 ו-−3. הפירוק הוא (x − 2)(x − 3). פתיחה של המכפלה מחזירה את x² − 5x + 6.",
  },
  "algebra-system-two-equations": {
    skillId: "algebra.system",
    gradeMin: 8,
    gradeMax: 9,
    skillLabel: "מערכת משוואות",
    name: "פתרו את המערכת 2x + y = 7, x − y = 2",
    prompt:
      "פתרו את המערכת 2x + y = 7, x − y = 2\nפתרו עבור x ו-y:\n2x + y = 7\nx − y = 2",
    hints: [
      "חיבור שתי המשוואות מבטל את y.",
      "אחרי שמצאתם את x, הציבו באחת המשוואות.",
    ],
    explanation: "מחברים את המשוואות ומקבלים 3x = 9, לכן x = 3. ואז y = 7 − 6 = 1.",
  },
  "algebra-quadratic-vertex-form": {
    skillId: "algebra.vertex-form",
    gradeMin: 11,
    gradeMax: 12,
    skillLabel: "צורת קודקוד",
    name: "צורת קודקוד של פונקציה ריבועית",
    prompt:
      "צורת קודקוד של פונקציה ריבועית\nנתונה הפונקציה הריבועית f(x) = 2x^2 - 8x + 5\n1. כתבו את הפונקציה בצורת קודקוד.\n2. ציינו את שיעורי הקודקוד.\n3. קבעו את ציר הסימטריה.\n4. מצאו את חותך ציר ה-y.\n5. שרטטו סקיצה של הפרבולה.",
    hints: [
      "הוציאו 2 כגורם משותף מאיברי ה-x לפני השלמה לריבוע.",
      "הקודקוד הוא (h, k) מתוך a(x − h)² + k.",
    ],
    explanation:
      "השלמה לריבוע: 2(x² − 4x) + 5 = 2((x − 2)² − 4) + 5 = 2(x − 2)² − 3.",
    parts: [
      {
        id: "1",
        text: "כתבו את הפונקציה בצורת קודקוד.",
        hints: ["הוציאו 2 כגורם משותף מאיברי ה-x לפני השלמה לריבוע."],
      },
      {
        id: "2",
        text: "ציינו את שיעורי הקודקוד.",
        hints: ["הקודקוד הוא (h, k) מתוך a(x − h)² + k."],
      },
      {
        id: "3",
        text: "קבעו את ציר הסימטריה.",
        hints: ["ציר הסימטריה הוא הישר האנכי שעובר בקודקוד."],
      },
      {
        id: "4",
        text: "מצאו את חותך ציר ה-y.",
        hints: [
          "חותך ציר ה-y הוא f(0). כתבו אותו כנקודה על ציר ה-y, לא רק y = מספר.",
        ],
      },
      {
        id: "5",
        text: "שרטטו סקיצה של הפרבולה.",
        hints: [
          "סמנו את הקודקוד, הציר וחותך ציר ה-y. הפרבולה נפתחת לפי סימן a.",
        ],
      },
    ],
  },
  "geometry-triangle-area": {
    skillId: "geometry.triangle-area",
    gradeMin: 5,
    gradeMax: 6,
    skillLabel: "שטח משולש",
    name: "שטח של משולש",
    prompt: "שטח של משולש\nמצאו את שטח המשולש.\nנתון:\nבסיס=8\nh = 6",
    hints: [
      "שטח משולש הוא מחצית הבסיס כפול הגובה.",
      "כאן הבסיס הוא 8 והגובה הוא 6.",
    ],
    explanation: "A = (1/2) × 8 × 6 = 24.",
    annotations: { "base=8": "בסיס=8" },
  },
  "geometry-pythagorean-hypotenuse": {
    skillId: "geometry.pythagorean",
    gradeMin: 8,
    gradeMax: 9,
    skillLabel: "משפט פיתגורס",
    name: "משפט פיתגורס — מצאו את היתר",
    prompt:
      "משפט פיתגורס — מצאו את היתר\nבמשולש ABC מצאו את אורך היתר c.\nנתון:\n∠C = 90°\na = 3\nb = 4\nc = ?",
    hints: [
      "במשולש ישר-זווית, a² + b² = c².",
      "ריבעו את שני הניצבים, חברו, ואז הוציאו שורש.",
    ],
    explanation: "3² + 4² = 9 + 16 = 25, לכן c = 5.",
  },
  "geometry-circle-circumference": {
    skillId: "geometry.circle-circumference",
    gradeMin: 8,
    gradeMax: 9,
    skillLabel: "היקף מעגל",
    name: "היקף של מעגל",
    prompt:
      "היקף של מעגל\nרדיוס המעגל הוא 5. מצאו את ההיקף. השאירו את π בתשובה.\nנתון:\nr = 5",
    hints: ["ההיקף הוא 2πr.", "השאירו את π בתשובה במקום מספר עשרוני."],
    explanation: "C = 2πr = 2π × 5 = 10π.",
  },
  "trig-exact-values-30-60": {
    skillId: "trig.exact-30-60",
    gradeMin: 10,
    gradeMax: 11,
    skillLabel: "ערכים מדויקים 30° ו-60°",
    name: "sin(30°) ו-cos(60°)",
    prompt:
      "sin(30°) ו-cos(60°)\nמצאו ערכים מדויקים (בלי מחשבון):\nsin(30°) =\ncos(60°) =",
    hints: [
      "אלה זוויות מיוחדות ממשולש 30-60-90.",
      "sin 30° ו-cos 60° הם אותו ערך מדויק.",
    ],
    explanation:
      "במשולש 30-60-90 הצלע שמול 30° היא חצי מהיתר, לכן sin 30° = 1/2. הצלע שליד 60° היא אותו חצי, לכן גם cos 60° = 1/2.",
  },
  "trig-right-triangle-ratios": {
    skillId: "trig.right-ratio",
    gradeMin: 10,
    gradeMax: 11,
    skillLabel: "יחסים במשולש ישר-זווית",
    name: "פתרון משולש ישר-זווית",
    prompt:
      "פתרון משולש ישר-זווית\nעבור הזווית θ בקודקוד B, מצאו sin θ, cos θ ו-tan θ.\nנתון:\n90°\nθ\nמול = 3\nליד = 4\nיתר = 5",
    hints: [
      "סינוס הוא ניצב מול חלקי יתר.",
      "θ נמצאת ב-B, ולכן הצלע שמול θ היא AC = 3.",
    ],
    explanation: "sin θ = 3/5, cos θ = 4/5, tan θ = 3/4.",
    annotations: {
      "opp = 3": "מול = 3",
      "adj = 4": "ליד = 4",
      "hyp = 5": "יתר = 5",
    },
  },
  "trig-unit-circle-cos-0": {
    skillId: "trig.exact-axis",
    gradeMin: 10,
    gradeMax: 11,
    skillLabel: "ערכים על צירי המעגל",
    name: "cos(0°) על מעגל היחידה",
    prompt: "cos(0°) על מעגל היחידה\nעל מעגל היחידה, מצאו את הערך המדויק של cos(0°).",
    hints: [
      "קוסינוס הוא שיעור ה-x של הנקודה על מעגל היחידה.",
      "0° היא הנקודה (1, 0).",
    ],
    explanation:
      "0° מתחיל על הכיוון החיובי של ציר x במעגל היחידה, הנקודה (1, 0). קוסינוס קורא את שיעור x, לכן cos 0° = 1. סינוס היה קורא את שיעור y, שהוא 0.",
  },
  "calculus-derivative-power": {
    skillId: "calc.power-rule",
    gradeMin: 11,
    gradeMax: 12,
    skillLabel: "כלל החזקה",
    name: "נגזרת של x³",
    prompt: "נגזרת של x³\nגזרו לפי x:\nd/dx(x^3)",
    hints: [
      "כלל החזקה: מורידים את המעריך ומחסרים ממנו 1.",
      "הנגזרת של xⁿ היא n xⁿ⁻¹.",
    ],
    explanation:
      "כלל החזקה כופל במעריך ואז מקטין אותו באחד. המעריך של x³ הוא 3, לכן הנגזרת היא 3x².",
  },
  "calculus-integral-power": {
    skillId: "calc.indefinite-integral",
    gradeMin: 11,
    gradeMax: 12,
    skillLabel: "אינטגרל לא מסוים",
    name: "חשבו את ∫ x dx",
    prompt: "חשבו את ∫ x dx\nחשבו את האינטגרל הלא מסוים:\n∫x dx",
    hints: [
      "הפכו את כלל החזקה: מעלים את החזקה ב-1, ואז מחלקים בחזקה החדשה.",
      "באינטגרל לא מסוים צריך קבוע אינטגרציה.",
    ],
    explanation:
      "מעלים את החזקה של x מ-1 ל-2, ואז מחלקים בחזקה החדשה. הפונקציה הקדומה היא x²/2, וזה אותו דבר כמו (1/2)x². באינטגרל לא מסוים מוסיפים גם + C, כי הנגזרת של קבוע היא 0.",
  },
  "calculus-chain-rule": {
    skillId: "calc.chain-rule",
    gradeMin: 11,
    gradeMax: 12,
    skillLabel: "כלל השרשרת",
    name: "כלל השרשרת עבור (2x + 1)³",
    prompt: "כלל השרשרת עבור (2x + 1)³\nגזרו לפי x:\n(2x+1)^3",
    hints: [
      "כלל השרשרת: נגזרת הפונקציה החיצונית כפול נגזרת הפונקציה הפנימית.",
      "הפונקציה הפנימית היא 2x + 1, והנגזרת שלה היא 2.",
    ],
    explanation:
      "החזקה החיצונית היא 3, לכן הנגזרת החיצונית היא 3(2x + 1)². הנגזרת הפנימית של 2x + 1 היא 2. כופלים: 3 · 2 · (2x + 1)² = 6(2x + 1)².",
  },
  "statistics-mean": {
    skillId: "stats.mean",
    gradeMin: 5,
    gradeMax: 6,
    skillLabel: "ממוצע",
    name: "ממוצע של סדרת נתונים",
    prompt: "ממוצע של סדרת נתונים\nמצאו את הממוצע:\n4,  6,  8,  10,  12",
    hints: [
      "הממוצע הוא סכום הערכים חלקי מספרם.",
      "ברשימה הזו יש 5 מספרים.",
    ],
    explanation: "(4 + 6 + 8 + 10 + 12) / 5 = 40 / 5 = 8.",
  },
  "statistics-independent-events": {
    skillId: "stats.independent-events",
    gradeMin: 8,
    gradeMax: 9,
    skillLabel: "מאורעות בלתי תלויים",
    name: "הסתברות של שני מאורעות בלתי תלויים",
    prompt:
      "הסתברות של שני מאורעות בלתי תלויים\nמטילים מטבע הוגן פעמיים, ואז מטילים קובייה הוגנת.\nהמאורעות בלתי תלויים. מצאו P(עץ, עץ, זוגי).",
    hints: [
      "במאורעות בלתי תלויים מכפילים את ההסתברויות הנפרדות.",
      "P(עץ) = 1/2 ו-P(זוגי בקובייה) = 1/2.",
    ],
    explanation: "(1/2) × (1/2) × (1/2) = 1/8.",
  },
  "statistics-median": {
    skillId: "stats.median",
    gradeMin: 5,
    gradeMax: 6,
    skillLabel: "חציון",
    name: "חציון של סדרת נתונים",
    prompt: "חציון של סדרת נתונים\nמצאו את החציון:\n3,  12,  4,  9,  7",
    hints: ["סדרו קודם את המספרים.", "החציון הוא הערך האמצעי ברשימה הממוינת."],
    explanation: "לפי הסדר: 3, 4, 7, 9, 12. הערך האמצעי הוא 7.",
  },
  "general-order-of-operations": {
    skillId: "arithmetic.pemdas",
    gradeMin: 5,
    gradeMax: 6,
    skillLabel: "סדר פעולות",
    name: "סדר פעולות",
    prompt: "סדר פעולות\nחשבו:\n3+4×2",
    hints: ["כופלים לפני שמחברים.", "4 × 2 הוא 8, ואז מוסיפים 3."],
    explanation: "3 + 4 × 2 = 3 + 8 = 11.",
  },
  "general-percent-increase": {
    skillId: "arithmetic.percent-increase",
    gradeMin: 7,
    gradeMax: 8,
    skillLabel: "הגדלה באחוזים",
    name: "אחוז עלייה וירידה",
    prompt: "אחוז עלייה וירידה\nמחיר בחנות עלה מ-$40 ל-$50.\nמה אחוז העלייה?",
    hints: [
      "אחוז עלייה הוא השינוי חלקי הסכום המקורי.",
      "השינוי הוא 10, והמחיר המקורי הוא 40.",
    ],
    explanation: "(50 − 40) / 40 = 10/40 = 25%.",
  },
  "general-fraction-of-quantity": {
    skillId: "arithmetic.fraction-of",
    gradeMin: 5,
    gradeMax: 6,
    skillLabel: "שבר מכמות",
    name: "שבר מכמות",
    prompt: "שבר מכמות\nבשקית יש 20 תפוחים. מהו 3/4 מ-20?",
    hints: ["״של״ כאן פירושו כפל: (3/4) × 20.", "אפשר גם לחשב 20 ÷ 4, ואז לכפול ב-3."],
    explanation: "(3/4) × 20 = 15.",
  },
  "algebra-linear-inequality": {
    skillId: "algebra.linear-inequality",
    gradeMin: 8,
    gradeMax: 9,
    skillLabel: "אי-שוויון קווי",
    name: "פתרו 3x − 5 > 7",
    prompt: "פתרו 3x − 5 > 7\nפתרו את האי-שוויון:\n3x-5>7",
    hints: [
      "מוסיפים 5 לשני האגפים, כמו במשוואה.",
      "אחר כך מחלקים ב-3. כיוון האי-שוויון נשאר.",
    ],
    explanation: "3x > 12, ולכן x > 4.",
  },
  "algebra-expand-binomial": {
    skillId: "algebra.expand-binomial",
    gradeMin: 7,
    gradeMax: 8,
    skillLabel: "פתיחת בינום",
    name: "פתחו את (x + 2)(x + 3)",
    prompt: "פתחו את (x + 2)(x + 3)\nפתחו:\n(x+2)(x+3)",
    hints: [
      "השתמשו בפיזור, או בכפל כל איבר בגורם הראשון.",
      "האיברים האמצעיים הם 3x ו-2x.",
    ],
    explanation: "x² + 3x + 2x + 6 = x² + 5x + 6.",
  },
  "algebra-slope-two-points": {
    skillId: "algebra.slope",
    gradeMin: 8,
    gradeMax: 9,
    skillLabel: "שיפוע",
    name: "שיפוע דרך (1, 2) ו-(3, 8)",
    prompt: "שיפוע דרך (1, 2) ו-(3, 8)\nמצאו את שיפוע הישר דרך (1, 2) ו-(3, 8).",
    hints: [
      "השיפוע הוא (y₂ − y₁) / (x₂ − x₁).",
      "השינוי ב-y הוא 6 והשינוי ב-x הוא 2.",
    ],
    explanation: "m = (8 − 2) / (3 − 1) = 6/2 = 3.",
  },
  "geometry-triangle-angle-sum": {
    skillId: "geometry.angle-sum",
    gradeMin: 5,
    gradeMax: 6,
    skillLabel: "סכום זוויות במשולש",
    name: "זווית חסרה במשולש",
    prompt: "זווית חסרה במשולש\nבמשולש יש זוויות 50° ו-60°. מצאו את הזווית השלישית.",
    hints: ["סכום הזוויות במשולש הוא 180°.", "חסרו 50° ו-60° מ-180°."],
    explanation: "180° − 50° − 60° = 70°.",
  },
  "geometry-circle-area": {
    skillId: "geometry.circle-area",
    gradeMin: 8,
    gradeMax: 9,
    skillLabel: "שטח מעגל",
    name: "שטח של מעגל",
    prompt:
      "שטח של מעגל\nרדיוס המעגל הוא 3. מצאו את השטח. השאירו את π בתשובה.\nנתון:\nr = 3",
    hints: ["שטח מעגל הוא πr².", "השאירו את π בתשובה במקום מספר עשרוני."],
    explanation: "A = πr² = π × 3² = 9π.",
  },
  "geometry-similar-triangles": {
    skillId: "geometry.similar-triangles",
    gradeMin: 10,
    gradeMax: 11,
    skillLabel: "דמיון משולשים",
    name: "משולשים דומים — צלע חסרה",
    prompt:
      "משולשים דומים — צלע חסרה\nהמשולשים דומים. צלעות מתאימות 4 ו-8, וגם 5 ו-x. מצאו את x.",
    hints: [
      "במשולשים דומים הצלעות המתאימות פרופורציוניות.",
      "מקדם הדמיון מ-4 ל-8 הוא 2.",
    ],
    explanation: "8/4 = 2, לכן x = 2 × 5 = 10.",
  },
  "trig-tan-45": {
    skillId: "trig.tan-45",
    gradeMin: 10,
    gradeMax: 11,
    skillLabel: "טנגנס 45°",
    name: "tan(45°)",
    prompt: "tan(45°)\nמצאו את הערך המדויק של tan(45°). בלי מחשבון.",
    hints: ["tan θ = sin θ / cos θ.", "ב-45° הסינוס והקוסינוס שווים."],
    explanation:
      "טנגנס הוא סינוס חלקי קוסינוס. ב-45° הניצב שמול והניצב שליד שווים, לכן הסינוס והקוסינוס שווים והמנה שלהם היא 1. tan 45° = 1.",
  },
  "trig-sin-90": {
    skillId: "trig.exact-axis",
    gradeMin: 10,
    gradeMax: 11,
    skillLabel: "ערכים על צירי המעגל",
    name: "sin(90°)",
    prompt: "sin(90°)\nעל מעגל היחידה, מצאו את הערך המדויק של sin(90°).",
    hints: [
      "סינוס הוא שיעור ה-y של הנקודה על מעגל היחידה.",
      "90° היא ראש המעגל, הנקודה (0, 1).",
    ],
    explanation:
      "90° היא ראש מעגל היחידה, הנקודה (0, 1). סינוס קורא את שיעור y, לכן sin 90° = 1. קוסינוס קורא את שיעור x, שהוא 0 שם.",
  },
  "trig-acute-from-sine": {
    skillId: "trig.inverse-special",
    gradeMin: 10,
    gradeMax: 11,
    skillLabel: "פונקציה הפוכה לזווית מיוחדת",
    name: "זווית חדה עם sin θ = 1/2",
    prompt: "זווית חדה עם sin θ = 1/2\nθ חדה ו-sin θ = 1/2. מצאו את θ.",
    hints: [
      "זו זווית מיוחדת ממשולש 30-60-90.",
      "sin 30° = 1/2. זווית חדה היא בין 0° ל-90°.",
    ],
    explanation:
      "sin 30° = 1/2, מן הניצב הקצר במשולש 30-60-90. סינוס הפוך של יחס חיובי, בטווח החד הרגיל, מחזיר את הזווית הזו. θ = 30°.",
  },
  "calculus-derivative-5x2": {
    skillId: "calc.power-rule",
    gradeMin: 11,
    gradeMax: 12,
    skillLabel: "כלל החזקה",
    name: "נגזרת של 5x²",
    prompt: "נגזרת של 5x²\nגזרו לפי x:\n5x^2",
    hints: [
      "כלל החזקה: n xⁿ⁻¹, ואז כפל ב-5 שמלפנים.",
      "הנגזרת של x² היא 2x.",
    ],
    explanation:
      "הקבוע 5 נשאר מלפנים. כלל החזקה הופך את x² ל-2x. אחר כך 5 · 2x = 10x, לכן הנגזרת היא 10x.",
  },
  "calculus-product-rule": {
    skillId: "calc.product-rule",
    gradeMin: 11,
    gradeMax: 12,
    skillLabel: "כלל המכפלה",
    name: "כלל המכפלה עבור x(x + 1)",
    prompt: "כלל המכפלה עבור x(x + 1)\nגזרו לפי x:\nx(x+1)",
    hints: [
      "כלל המכפלה: u′v + uv′.",
      "אפשר גם לפתוח קודם: x² + x, ואז לגזור.",
    ],
    explanation:
      "קודם פותחים: x(x + 1) = x² + x, והנגזרת של זה היא 2x + 1. לפי כלל המכפלה מתקבל אותו דבר: 1·(x + 1) + x·1 = x + 1 + x = 2x + 1.",
  },
  "calculus-definite-integral": {
    skillId: "calc.definite-integral",
    gradeMin: 11,
    gradeMax: 12,
    skillLabel: "אינטגרל מסוים",
    name: "חשבו את ∫₀² 2x dx",
    prompt: "חשבו את ∫₀² 2x dx\nחשבו את האינטגרל המסוים של 2x dx מ-0 עד 2.",
    hints: [
      "הפונקציה הקדומה של 2x היא x².",
      "הציבו את הגבול העליון, ואז חסרו את הגבול התחתון.",
    ],
    explanation:
      "פונקציה קדומה של 2x היא x². מציבים את הגבול העליון ומחסרים את הערך בגבול התחתון: 2² − 0² = 4 − 0 = 4.",
  },
  "statistics-range": {
    skillId: "stats.range",
    gradeMin: 5,
    gradeMax: 6,
    skillLabel: "טווח",
    name: "טווח של סדרת נתונים",
    prompt: "טווח של סדרת נתונים\nמצאו את הטווח:\n3,  12,  4,  9,  7",
    hints: ["הטווח הוא הערך הגדול ביותר פחות הקטן ביותר.", "הגדול ביותר הוא 12 והקטן ביותר הוא 3."],
    explanation: "12 − 3 = 9.",
  },
  "statistics-mode": {
    skillId: "stats.mode",
    gradeMin: 5,
    gradeMax: 6,
    skillLabel: "שכיח",
    name: "שכיח של סדרת נתונים",
    prompt: "שכיח של סדרת נתונים\nמצאו את השכיח:\n2,  5,  3,  5,  7,  5,  2",
    hints: ["השכיח הוא הערך שמופיע הכי הרבה פעמים.", "ספרו כמה פעמים כל מספר מופיע."],
    explanation: "5 מופיע שלוש פעמים, יותר מכל ערך אחר.",
  },
  "statistics-die-even": {
    skillId: "stats.sample-space",
    gradeMin: 5,
    gradeMax: 6,
    skillLabel: "מרחב מדגם",
    name: "הסתברות לתוצאה זוגית",
    prompt: "הסתברות לתוצאה זוגית\nמטילים קובייה הוגנת בת שש פאות. מצאו P(זוגי).",
    hints: [
      "כתבו את ההסתברות לפאה זוגית כשבר: מספר הפאות הזוגיות חלקי מספר הצדדים.",
      "הפאות הזוגיות הן 2, 4 ו-6.",
    ],
    explanation: "3 פאות זוגיות מתוך 6, לכן 3/6 = 1/2.",
  },
  "general-power-of-two": {
    skillId: "arithmetic.power-of-two",
    gradeMin: 5,
    gradeMax: 6,
    skillLabel: "חזקות של 2",
    name: "חשבו את 2⁵",
    prompt: "חשבו את 2⁵\nחשבו:\n2^5",
    hints: ["2⁵ פירושו חמישה גורמים של 2 זה בזה.", "2 × 2 × 2 × 2 × 2."],
    explanation: "2⁵ = 32.",
  },
  "general-percent-of": {
    skillId: "arithmetic.percent-of",
    gradeMin: 5,
    gradeMax: 6,
    skillLabel: "אחוז מכמות",
    name: "15% מ-80",
    prompt: "15% מ-80\nמהו 15% מ-80?",
    hints: [
      "אחוז הוא מאית: 15% = 15/100.",
      "אפשר גם לקחת 10% מ-80 ומחצית מזה, ואז לחבר.",
    ],
    explanation: "0.15 × 80 = 12.",
  },
  "general-ratio-parts": {
    skillId: "arithmetic.ratio-parts",
    gradeMin: 7,
    gradeMax: 8,
    skillLabel: "חלוקה ביחס",
    name: "חלוקה ביחס 3:5",
    prompt: "חלוקה ביחס 3:5\n$40 מתחלקים ביחס 3:5. כמה מקבל החלק הקטן?",
    hints: ["היחס 3:5 כולל 3 + 5 = 8 חלקים.", "החלק הקטן הוא 3 מתוך 8 החלקים."],
    explanation: "כל חלק הוא 40/8 = 5, לכן 3 חלקים הם 15.",
  },
  "vectors-add-components": {
    skillId: "vectors.add",
    gradeMin: 11,
    gradeMax: 12,
    skillLabel: "חיבור וקטורים",
    name: "חברו ⟨3, 1⟩ + ⟨2, 4⟩",
    prompt: "חברו ⟨3, 1⟩ + ⟨2, 4⟩\nחשבו ⟨3, 1⟩ + ⟨2, 4⟩.",
    hints: ["מחברים רכיבים תואמים: x עם x, y עם y.", "הרכיבים הראשונים הם 3 ו-2."],
    explanation: "⟨3 + 2, 1 + 4⟩ = ⟨5, 5⟩.",
  },
  "vectors-magnitude-3-4": {
    skillId: "vectors.magnitude",
    gradeMin: 11,
    gradeMax: 12,
    skillLabel: "אורך וקטור",
    name: "גודל של ⟨3, 4⟩",
    prompt: "גודל של ⟨3, 4⟩\nמצאו את הגודל של ⟨3, 4⟩.",
    hints: ["הגודל הוא √(x² + y²).", "זה משולש ישר-זווית 3-4-5."],
    explanation: "√(3² + 4²) = √(9 + 16) = √25 = 5.",
  },
  "vectors-scalar-multiply": {
    skillId: "vectors.scalar",
    gradeMin: 11,
    gradeMax: 12,
    skillLabel: "כפל בסקלר",
    name: "כפל בסקלר 3⟨2, −1⟩",
    prompt: "כפל בסקלר 3⟨2, −1⟩\nחשבו 3⟨2, −1⟩.",
    hints: ["כופלים כל רכיב בסקלר 3.", "רכיב ה-y שלילי, ולכן הוא נשאר שלילי."],
    explanation: "⟨3 · 2, 3 · (−1)⟩ = ⟨6, −3⟩.",
  },
  "vectors-dot-product": {
    skillId: "vectors.dot",
    gradeMin: 11,
    gradeMax: 12,
    skillLabel: "מכפלה סקלרית",
    name: "מכפלה סקלרית ⟨2, 3⟩ · ⟨4, 1⟩",
    prompt: "מכפלה סקלרית ⟨2, 3⟩ · ⟨4, 1⟩\nמצאו ⟨2, 3⟩ · ⟨4, 1⟩.",
    hints: ["המכפלה הסקלרית היא x₁x₂ + y₁y₂.", "כופלים רכיבים תואמים, ואז מחברים."],
    explanation: "2·4 + 3·1 = 8 + 3 = 11.",
  },
  "complex-i-squared": {
    skillId: "complex.powers-of-i",
    gradeMin: 11,
    gradeMax: 12,
    skillLabel: "חזקות של i",
    name: "היחידה המדומה i²",
    prompt: "היחידה המדומה i²\nפשטו את i².",
    hints: [
      "i מוגדר כך ש-i² = −1.",
      "זו ההגדרה של היחידה המדומה, לא ערך ממחשבון.",
    ],
    explanation:
      "היחידה המדומה מוגדרת על ידי הכלל i² = −1. פישוט של i² נעצר שם: הערך הוא −1, לא קירוב עשרוני.",
  },
  "complex-i-cubed": {
    skillId: "complex.powers-of-i",
    gradeMin: 11,
    gradeMax: 12,
    skillLabel: "חזקות של i",
    name: "פשטו את i³",
    prompt: "פשטו את i³\nפשטו את i³.",
    hints: ["כתבו i³ בתור i² · i.", "i² הוא −1, ולכן כופלים אותו ב-i."],
    explanation:
      "מפצלים את החזקה: i³ = i² · i. מחליפים את i² ב-−1, ואז כופלים בגורם i שנשאר: (−1) · i = −i.",
  },
  "complex-add": {
    skillId: "complex.add",
    gradeMin: 11,
    gradeMax: 12,
    skillLabel: "חיבור מספרים מרוכבים",
    name: "חברו (3 + 2i) + (1 − 4i)",
    prompt: "חברו (3 + 2i) + (1 − 4i)\nחשבו (3 + 2i) + (1 − 4i).",
    hints: ["מחברים את החלקים הממשיים ואת החלקים המדומים בנפרד.", "החלקים הממשיים הם 3 ו-1."],
    explanation: "(3 + 1) + (2 − 4)i = 4 − 2i.",
  },
  "complex-modulus": {
    skillId: "complex.modulus",
    gradeMin: 11,
    gradeMax: 12,
    skillLabel: "מודולוס",
    name: "מודולוס של 3 + 4i",
    prompt: "מודולוס של 3 + 4i\nמצאו את |3 + 4i|.",
    hints: [
      "המודולוס |a + bi| הוא √(a² + b²).",
      "זה אותו משולש 3-4-5 כמו בגודל של וקטור.",
    ],
    explanation: "|3 + 4i| = √(9 + 16) = √25 = 5.",
  },
  "algebra-add-one-step": {
    skillId: "algebra.linear-one-step",
    gradeMin: 7,
    gradeMax: 8,
    skillLabel: "משוואה בשלב אחד",
    name: "פתרו x + 5 = 12",
    prompt: "פתרו x + 5 = 12\nפתרו עבור x:\nx+5=12",
    hints: ["בטלו את החיבור כדי ש-x יישאר לבדו.", "חסרו את אותו מספר משני האגפים."],
    explanation: "מחסרים 5 משני האגפים: x = 12 − 5 = 7.",
  },
  "algebra-subtract-one-step": {
    skillId: "algebra.linear-one-step",
    gradeMin: 7,
    gradeMax: 8,
    skillLabel: "משוואה בשלב אחד",
    name: "פתרו x − 3 = 8",
    prompt: "פתרו x − 3 = 8\nפתרו עבור x:\nx-3=8",
    hints: ["בטלו את החיסור כדי ש-x יישאר לבדו.", "הוסיפו את אותו מספר לשני האגפים."],
    explanation: "מוסיפים 3 לשני האגפים: x = 8 + 3 = 11.",
  },
  "algebra-multiply-one-step": {
    skillId: "algebra.linear-one-step",
    gradeMin: 7,
    gradeMax: 8,
    skillLabel: "משוואה בשלב אחד",
    name: "פתרו 4x = 20",
    prompt: "פתרו 4x = 20\nפתרו עבור x:\n4x=20",
    hints: ["x מוכפל ב-4.", "חלקו את שני האגפים במקדם."],
    explanation: "מחלקים את שני האגפים ב-4: x = 20 / 4 = 5.",
  },
  "algebra-divide-one-step": {
    skillId: "algebra.linear-one-step",
    gradeMin: 7,
    gradeMax: 8,
    skillLabel: "משוואה בשלב אחד",
    name: "פתרו x / 3 = 4",
    prompt: "פתרו x / 3 = 4\nפתרו עבור x:\nx/3=4",
    hints: ["x מחולק ב-3.", "כפלו את שני האגפים במחלק."],
    explanation: "כופלים את שני האגפים ב-3: x = 4 × 3 = 12.",
  },
  "algebra-square-equals": {
    skillId: "algebra.square-root",
    gradeMin: 10,
    gradeMax: 11,
    skillLabel: "משוואה עם שורש",
    name: "פתרו x² = 49",
    prompt: "פתרו x² = 49\nפתרו עבור x:\nx^2=49",
    hints: ["הוציאו שורש משני האגפים.", "למספר חיובי יש שני שורשים."],
    explanation: "√49 = 7, וגם 7 וגם −7 בריבוע נותנים 49, לכן x = ±7.",
  },
  "algebra-slope-intercept": {
    skillId: "algebra.slope-intercept",
    gradeMin: 8,
    gradeMax: 9,
    skillLabel: "שיפוע וחותך",
    name: "ישר עם שיפוע 3 וחותך 2",
    prompt:
      "ישר עם שיפוע 3 וחותך 2\nכתבו את משוואת הישר:\nשיפוע 3, חותך ציר ה-y הוא 2",
    hints: ["צורת שיפוע-חותך היא y = mx + b.", "m הוא השיפוע ו-b הוא חותך ציר ה-y."],
    explanation: "מציבים m = 3 ו-b = 2 במשוואה y = mx + b.",
  },
  "geometry-rectangle-area": {
    skillId: "geometry.rectangle-area",
    gradeMin: 4,
    gradeMax: 5,
    skillLabel: "שטח מלבן",
    name: "שטח מלבן",
    prompt: "שטח מלבן\nמצאו את שטח המלבן.\n5 על 4",
    hints: ["שטח מלבן הוא אורך כפול רוחב.", "כפלו את שני אורכי הצלעות המסומנים."],
    explanation: "שטח = 5 × 4 = 20.",
  },
  "geometry-rectangle-perimeter": {
    skillId: "geometry.rectangle-perimeter",
    gradeMin: 4,
    gradeMax: 5,
    skillLabel: "היקף מלבן",
    name: "היקף מלבן",
    prompt: "היקף מלבן\nמצאו את היקף המלבן.\n6 על 3",
    hints: ["ההיקף הוא המרחק מסביב.", "חברו את ארבע הצלעות. צלעות נגדיות שוות."],
    explanation: "היקף = 2 × (6 + 3) = 18.",
  },
  "geometry-box-volume": {
    skillId: "geometry.box-volume",
    gradeMin: 7,
    gradeMax: 8,
    skillLabel: "נפח תיבה",
    name: "נפח תיבה",
    prompt: "נפח תיבה\nמצאו את נפח התיבה.\nאורך 2, רוחב 3, גובה 4",
    hints: ["נפח תיבה הוא אורך × רוחב × גובה.", "כפלו את שלושת אורכי המקצועות."],
    explanation: "נפח = 2 × 3 × 4 = 24.",
  },
  "geometry-distance-3-4": {
    skillId: "geometry.distance",
    gradeMin: 10,
    gradeMax: 11,
    skillLabel: "מרחק בין נקודות",
    name: "מרחק בין (0, 0) ל-(3, 4)",
    prompt:
      "מרחק בין (0, 0) ל-(3, 4)\nמצאו את המרחק בין הנקודות.\n(0, 0) ו-(3, 4)",
    hints: [
      "נוסחת המרחק היא √((x₂ − x₁)² + (y₂ − y₁)²).",
      "ההפרשים האופקי והאנכי יוצרים משולש ישר-זווית.",
    ],
    explanation: "√(3² + 4²) = √(9 + 16) = √25 = 5.",
  },
  "geometry-trapezoid-area": {
    skillId: "geometry.trapezoid-area",
    gradeMin: 10,
    gradeMax: 11,
    skillLabel: "שטח טרפז",
    name: "שטח טרפז",
    prompt: "שטח טרפז\nמצאו את שטח הטרפז.\nבסיסים 6 ו-10, גובה 4",
    hints: [
      "שטח טרפז הוא ממוצע שני הבסיסים כפול הגובה.",
      "מצאו קודם את ממוצע הבסיסים, ואז כפלו בגובה.",
    ],
    explanation: "שטח = (6 + 10) / 2 × 4 = 8 × 4 = 32.",
  },
  "geometry-midpoint": {
    skillId: "geometry.midpoint",
    gradeMin: 10,
    gradeMax: 11,
    skillLabel: "נקודת אמצע",
    name: "אמצע הקטע בין (2, 4) ל-(6, 8)",
    prompt: "אמצע הקטע בין (2, 4) ל-(6, 8)\nמצאו את נקודת האמצע.\n(2, 4) ו-(6, 8)",
    hints: ["נקודת האמצע היא ממוצע שיעורי ה-x וממוצע שיעורי ה-y.", "מצעו כל שיעור בנפרד."],
    explanation: "אמצע = ((2 + 6) / 2, (4 + 8) / 2) = (4, 6).",
  },
  "general-unit-price": {
    skillId: "arithmetic.unit-price",
    gradeMin: 6,
    gradeMax: 6,
    skillLabel: "מחיר ליחידה",
    name: "מחיר של פריט אחד",
    prompt: "מחיר של פריט אחד\nארבעה פריטים זהים עולים 12.\nמצאו את המחיר של פריט אחד.",
    hints: ["חלקו את המחיר הכולל במספר הפריטים.", "כל פריט עולה אותו סכום."],
    explanation: "12 / 4 = 3.",
  },
  "general-percent-off": {
    skillId: "arithmetic.percent-off",
    gradeMin: 7,
    gradeMax: 8,
    skillLabel: "הנחה באחוזים",
    name: "הנחה של 20% ממחיר 50",
    prompt: "הנחה של 20% ממחיר 50\nמחיר 50 יורד ב-20%.\nמצאו את מחיר המבצע.",
    hints: ["מצאו קודם 20% מהמחיר המקורי.", "חסרו את ההנחה מהמחיר המקורי."],
    explanation: "20% מ-50 הם 10, ולכן מחיר המבצע הוא 50 − 10 = 40.",
  },
  "statistics-two-heads": {
    skillId: "stats.independent-events",
    gradeMin: 7,
    gradeMax: 8,
    skillLabel: "מאורעות בלתי תלויים",
    name: "שני עץ ברצף",
    prompt: "שני עץ ברצף\nמטילים מטבע הוגן פעמיים.\nמצאו את ההסתברות לשני עץ.",
    hints: ["כל הטלה בלתי תלויה, לכן כופלים את שתי ההסתברויות.", "ההסתברות לעץ בהטלה אחת היא 1/2."],
    explanation: "P(עץ, עץ) = (1/2) × (1/2) = 1/4.",
  },
  "statistics-complement": {
    skillId: "stats.complement",
    gradeMin: 8,
    gradeMax: 9,
    skillLabel: "מאורע משלים",
    name: "הסתברות המאורע המשלים",
    prompt: "הסתברות המאורע המשלים\nההסתברות לגשם היא 0.3.\nמצאו את ההסתברות שלא ירד גשם.",
    hints: ["מאורע והמשלים שלו מסתכמים ל-1.", "חסרו את ההסתברות הנתונה מ-1."],
    explanation: "P(אין גשם) = 1 − 0.3 = 0.7.",
  },
  "statistics-marbles": {
    skillId: "stats.sample-space",
    gradeMin: 7,
    gradeMax: 8,
    skillLabel: "מרחב מדגם",
    name: "הוצאת גולה אדומה",
    prompt:
      "הוצאת גולה אדומה\nבשק 3 גולות אדומות ו-7 גולות כחולות.\nמצאו את ההסתברות להוציא גולה אדומה.",
    hints: ["הסתברות היא מספר התוצאות הרצויות חלקי סך כל התוצאות.", "ספרו את הגולות האדומות ואת כל הגולות בשק."],
    explanation: "יש 10 גולות, ו-3 מהן אדומות, ולכן ההסתברות היא 3/10.",
  },
  "statistics-mutually-exclusive": {
    skillId: "stats.mutually-exclusive",
    gradeMin: 10,
    gradeMax: 11,
    skillLabel: "מאורעות זרים",
    name: "הסתברות ל-1 או 2 בקובייה",
    prompt:
      "הסתברות ל-1 או 2 בקובייה\nמטילים קובייה הוגנת פעם אחת.\nמצאו את ההסתברות לקבל 1 או 2.",
    hints: ["שתי התוצאות האלה לא יכולות לקרות יחד.", "חברו את ההסתברויות שלהן."],
    explanation: "P(1) + P(2) = 1/6 + 1/6 = 1/3.",
  },
  "statistics-combinations": {
    skillId: "stats.combinations",
    gradeMin: 11,
    gradeMax: 12,
    skillLabel: "צירופים",
    name: "דרכים לבחור 2 מתוך 5",
    prompt: "דרכים לבחור 2 מתוך 5\nכמה דרכים יש לבחור 2 פריטים מתוך 5?\nהסדר אינו חשוב.",
    hints: [
      "השתמשו בצירוף C(n, k) = n! / (k!(n − k)!).",
      "הסדר אינו חשוב, ולכן זה אינו תמורה.",
    ],
    explanation: "C(5, 2) = (5 × 4) / (2 × 1) = 10.",
  },
  "statistics-relative-frequency": {
    skillId: "stats.relative-frequency",
    gradeMin: 10,
    gradeMax: 11,
    skillLabel: "שכיחות יחסית",
    name: "שכיחות יחסית",
    prompt: "שכיחות יחסית\nתוצאה הופיעה 8 פעמים מתוך 40 ניסיונות.\nמצאו את השכיחות היחסית.",
    hints: ["שכיחות יחסית היא המספר חלקי מספר הניסיונות.", "כתבו את התוצאה כשבר מצומצם או כמספר עשרוני."],
    explanation: "8 / 40 = 1/5 = 0.2.",
  },
  "statistics-weighted-mean": {
    skillId: "stats.weighted-mean",
    gradeMin: 10,
    gradeMax: 11,
    skillLabel: "ממוצע משוקלל",
    name: "ממוצע משוקלל של ציונים",
    prompt: "ממוצע משוקלל של ציונים\nשני ציונים של 70 ושלושה ציונים של 90.\nמצאו את הממוצע.",
    hints: ["כפלו כל ציון במספר הפעמים שהוא מופיע.", "חלקו את הסכום במספר הציונים."],
    explanation: "(2 × 70 + 3 × 90) / 5 = (140 + 270) / 5 = 82.",
  },
};
